<template>
  <div style="min-height: calc(100vh - 158px);" class="card">
    <div style="background: #DBE7F2" class="card p-2 mb-1">
      <TitleButton :showBtn="false" title="Stock Adjustment"/>

      <DateQuerySetter class="date-filter" :inEnoughSpace="false" btnTitle="Go" @onClickGo="stockAdjustmentList">
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Project</label>
          <v-select placeholder="Select Project" v-model="projectId" :options="projects" label="name"
                    :reduce="name => name.id"/>
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Cost Center: </label>
          <v-select placeholder="Select Cost Centre" v-model="costCentreId" :options="costCentres" label="name"
                    :reduce="name => name.id"/>
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">
            Product/Service Type
          </label>
          <v-select
            placeholder="Select Product/Service Type"
            v-model="productType"
            :options="productTypes"
            label="text"
            :reduce="text => text.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label"> Product Group </label>
          <AsyncSelect
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="ancestorId"
            label="text"
            :reduce="text => text.id"
            :additional-query="groupQueryObj"
            :format-label="option => option.text"
            ref="groupSelector"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label"> Product/Service </label>
          <AsyncSelect
            placeholder="Select Product/Service"
            :api-service="fetchProductList"
            v-model="productId"
            label="text"
            :reduce="text => text.id"
            :additional-query="productQueryObj"
            :format-label="option => option.text"
            ref="productSelector"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label"> Warehouse </label>
          <v-select placeholder="Select Inventory Location" v-model="warehouseId" :options="locations" label="text"
                    :reduce="text => text.id"/>
        </div>
      </DateQuerySetter>
    </div>

    <div class="col-12">
      <div class="d-flex justify-content-end px-2">
        <button
            @click="exportTable"
            class="btn btn-outline-secondary btn-sm btn-download-invoice waves-effect"
        >
          Export
        </button>
      </div>
    </div>

    <div class="card mt-1 px-2">
      <ListTable :tableItems="stockAdjustments"/>
    </div>

    <div class="px-2">
      <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>
    <Loader v-if="reportLoading"/>
  </div>
</template>

<script setup>
import {onMounted, ref, defineEmits, computed, watch} from 'vue';
import TitleButton from '@/components/atom/TitleButton'
import DateQuerySetter from '@/components/atom/DateQuerySetter'
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleReport from '@/services/modules/inventory';
import handleBusinessesLocations from '@/services/modules/businessesLocations';
import handlePurchase from '@/services/modules/purchase';
import figureFormatter from "@/services/utils/figureFormatter";
import handleProjects from "@/services/modules/procurement/project";
import handleCostCentres from "@/services/modules/ngo/costCentre";
import {useRoute, useRouter} from "vue-router";
import ListTable from "@/components/molecule/company/stock-report/StockAdjustmentTable.vue";
import Pagination from '@/components/atom/Pagination'
import {useStore} from "vuex";
import Loader from "@/components/atom/LoaderComponent.vue";

const {fetchBusinessLocationsList} = handleBusinessesLocations();
const {fetchHome, getStockAdjustmentReport, reportLoading} = handleReport();
const {fetchProductList} = handlePurchase();
const {fetchProjects} = handleProjects();
const {fetchCostCentreList} = handleCostCentres();

const {showError, showSuccess} = defineEmits();
const $router = useRouter()
const $route = useRoute()
const $store = useStore()

const productId = ref(null);
const ancestorId = ref(null);
const warehouseId = ref(null);
const costCentreId = ref(null);
const projectId = ref(null);
const productType = ref(null);
const productSelector = ref(null);
const groupSelector = ref(null);

const locations = ref([]);
const productTypes = ref([]);
const costCentres = ref([]);
const projects = ref([]);

const stockAdjustments = ref([]);
const offset = 20;

const companyId = computed(() => $route.params.companyId);
const start = computed(() => $route.query.start);
const end = computed(() => $route.query.end);
const page = computed(() => $route.query.page);
const token = computed(() => localStorage.getItem('token'));
const productQueryObj = computed(() => {
  const queryObj = {};

  if (!!productType.value && productType.value !== '') {
    queryObj.product_type = productType.value;
  }

  if (!!ancestorId.value) {
    queryObj.ancestor_id = ancestorId.value;
  }

  return queryObj;

})
const groupQueryObj = computed(() => {
  const queryObj = {
    group_level: 1
  };

  if (!!productType.value) {
    queryObj.product_type = productType.value;
  }

  return queryObj;

})

const exportTable = () => {
  let query = getQuery();
  let url = `${process.env.VUE_APP_BASE_URL}/export/inventory/stock-adjustment${query}&_token=${token.value}`;
  let a = document.createElement("a");
  a.setAttribute("href", url);
  a.click();
};

const getQuery = () => {
  let query = '?company_id=' + companyId.value
  if (!!productId.value) query += '&product_id=' + productId.value
  if (!!warehouseId.value) query += '&warehouse_id=' + warehouseId.value
  if (!!costCentreId.value) query += '&cost_centre_id=' + costCentreId.value
  if (!!projectId.value) query += '&project_id=' + projectId.value
  if (!!productType.value) query += '&product_type=' + productType.value
  if (!!ancestorId.value) query += '&ancestor_id=' + ancestorId.value
  if (!start.value && !end.value) return query
  if (start.value) query += '&start_date=' + start.value
  if (end.value) query += '&end_date=' + end.value
  if (page.value) query += '&page=' + page.value
  query += '&offset=' + offset
  return query
};

const onPageChange = page => {
  let routeQuery = Object.assign({}, $route.query)
  routeQuery.page = page
  $router.push({path: $route.path, query: routeQuery})
  setTimeout(() => {
    stockAdjustmentList();
  }, 100)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
};

const setPaginationData = (data) => {
  $store.commit('setPaginationData', data)
};

const resetPagination = () => {
  $store.commit('resetPagination')
};

watch(ancestorId, () => {
  productSelector.value.clear();
  productId.value = null;
})

watch(productType, () => {
  productSelector.value.clear();
  groupSelector.value.clear();
  productId.value = null;
  ancestorId.value = null;
})

async function stockAdjustmentList() {
  try {
    const res = await getStockAdjustmentReport(getQuery());
    if (!res.status) {
      stockAdjustments.value = [];
      return resetPagination()
    }
    const {data, ...paginationData} = res.data;
    stockAdjustments.value = data;
    await setPagination(paginationData)
  } catch (err) {
    if (err.response) {
      showError(err.response.message)
    }
  }
}

onMounted(async () => {
  const companyQuery = `?company_id=${companyId.value}`;
  let projectQuery = companyQuery + '&with_donors=1';
  try {
    const [businessLocationRes, projectsRes, costCentresRes, homeRes] = await Promise.all([
      fetchBusinessLocationsList(companyQuery),
      fetchProjects(projectQuery),
      fetchCostCentreList(companyQuery),
      fetchHome()
    ]);

    if (homeRes.data && homeRes.data.product_type) {
      for (let key in homeRes.data.product_type) {
        productTypes.value.push({
          id : key,
          text : homeRes.data.product_type[key]
        });
      }
    }
    if (businessLocationRes.data) {
      locations.value = businessLocationRes.data;
      locations.value.unshift({
        id: '',
        text: 'All'
      });
    }
    if (projectsRes.data) {
      projects.value = projectsRes.data;
    }
    if (costCentresRes.data) {
      costCentres.value = costCentresRes.data;
    }
    location.value = 'all';
    reportLoading.value = false;
  } catch (error) {
    reportLoading.value = false;
  }
  await stockAdjustmentList()
});
</script>


<style scoped>
.head td {
  text-align: right;
}
@media (min-width: 768px) {
  .date-filter :deep(.col-lg-2) {
    flex: 0 0 auto;
    width: 33.333333%;
  }
}
@media (min-width: 992px) {
  .date-filter :deep(.col-lg-2) {
    width: 25%;
  }
}
@media (max-width: 767.98px) {
  .date-filter :deep(.action-btn-setter) {
    width: 100%;
  }
  .date-filter :deep(.action-btn-setter .btn) {
    width: 100%;
  }
}
</style>
